import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Send, Link2, CheckCircle2, Loader2 } from "lucide-react";

const err = (e) => toast.error(e?.response?.data?.detail || "Something went wrong");

const ITEMS = [
  ["tfr_cross", "TFR D cross (BIG 3×)"], ["tfr_adr", "TFR D + Range Matrix breakout"], ["open_vol", "Opening candle volume ≥3×"],
  ["bigvol", "Big Money Volume ≥3×"], ["adr", "Range Matrix Breakout"], ["summary", "After-close summary"],
];

const Settings = () => {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/telegram/alert-settings").then((r) => setC(r.data)).catch(() => {}); }, []);
  const save = (patch) => api.put("/telegram/alert-settings", patch).then((r) => { setC(r.data); toast.success("Alert settings saved"); }).catch(err);
  if (!c) return null;
  return (
    <div className="mt-3 border-t border-slate-200 pt-3 space-y-1.5" data-testid="tg-settings">
      {ITEMS.map(([k, label]) => (
        <label key={k} className="flex items-center justify-between text-xs font-mono text-slate-700 max-w-sm">
          {label}<Switch checked={!!c[k]} onCheckedChange={(v) => save({ [k]: v })} data-testid={`tg-set-${k}`} />
        </label>
      ))}
      <div className="flex items-center gap-1 text-xs font-mono text-slate-700 pt-1">
        <span className="mr-2">TFR cross candle</span>
        {["5m", "15m", "both"].map((t) => (
          <button key={t} onClick={() => save({ cross_tf: t })} data-testid={`tg-set-tf-${t}`}
            className={`h-6 px-2 border transition-colors ${c.cross_tf === t ? "bg-blue-600 text-white border-blue-600" : "border-slate-300 text-slate-500 hover:border-blue-500"}`}>{t}</button>
        ))}
      </div>
    </div>
  );
};

export const TelegramCrossAlerts = () => {
  const [s, setS] = useState(null);
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);
  const load = () => {
    api.get("/telegram/status").then((r) => setS(r.data)).catch(() => {});
    api.get("/telegram/cross-alerts").then((r) => setLog(r.data)).catch(() => {});
  };
  useEffect(load, []);
  const gen = () => api.post("/telegram/generate-code").then((r) => setLink(r.data.deep_link)).catch(err);
  const verify = async () => {
    setBusy(true);
    try {
      const r = await api.post("/telegram/verify");
      r.data.connected ? toast.success("Telegram linked") : toast.message(r.data.detail);
      load();
    } catch (e) { err(e); }
    setBusy(false);
  };
  const test = () => api.post("/telegram/test").then((r) => toast.message(`Test: ${r.data.status}`)).catch(err);
  const off = () => api.post("/telegram/disconnect").then(load).catch(err);
  if (!s) return null;
  return (
    <Card className="bg-white border-slate-200 p-4 mb-4" data-testid="tg-cross-card">
      <div className="flex items-center gap-2 mb-1"><Send className="h-4 w-4 text-cyan-600" />
        <span className="text-sm font-semibold text-slate-900">Telegram alerts</span>
        <span className={`text-[10px] font-mono px-1.5 border ${s.connected ? "border-emerald-500 text-emerald-500" : "border-slate-300 text-slate-500"}`} data-testid="tg-status">
          {!s.configured ? "BOT NOT SET UP" : s.connected ? "LINKED" : "NOT LINKED"}</span>
      </div>
      <p className="text-xs text-slate-500 mb-3">Every TFR D cross with BIG 3× (once per stock per line per day) · opening 5m candle ≥3× usual · Big Money Volume ≥3× · Quiet Mode: one grouped message every 5 min (Range Matrix breakouts sent instantly) · marked if in a Today list / best-2 or worst-2 sector · day's summary after 15:40 · only to you</p>
      {s.configured && !s.connected && (
        <div className="flex flex-wrap items-center gap-2">
          {!link ? <Button size="sm" onClick={gen} data-testid="tg-gen-link"><Link2 className="h-4 w-4 mr-1" />Link my Telegram</Button> : <>
            <a href={link} target="_blank" rel="noreferrer" className="text-xs font-mono text-blue-500 underline" data-testid="tg-deep-link">1. Open bot &amp; press Start</a>
            <Button size="sm" variant="outline" onClick={verify} disabled={busy} data-testid="tg-verify">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "2. I've pressed Start"}</Button>
          </>}
        </div>
      )}
      {s.connected && (
        <div className="flex flex-wrap items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          <Button size="sm" variant="outline" onClick={test} data-testid="tg-test">Send test alert</Button>
          <Button size="sm" variant="ghost" onClick={off} data-testid="tg-unlink">Unlink</Button>
        </div>
      )}
      <Settings />
      {log.length > 0 && (
        <div className="mt-3 text-[11px] font-mono text-slate-500 space-y-px max-h-32 overflow-y-auto" data-testid="tg-alert-log">
          {log.map((a) => <div key={a.id}>{a.id.split("|")[0]} {a.cross_at} · {a.tf} · {a.symbol} · {a.list} · {(a.status || []).join(",")}</div>)}
        </div>
      )}
    </Card>
  );
};
